/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useContext, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import {translate} from '@ion/core/src/locale/locale';
import {Crumb} from '@ion/core/src/types/entities';
import {getUserUnit} from '@ion/core/src/units/helpers';
import Unit from '@ion/core/src/units/units';
import {PrimaryButton} from '../../components/buttons/PrimaryButton';
import {TertiaryButton} from '../../components/buttons/TertiaryButton';
import Slider from '../../components/slider/Slider';
import TextField from '../../components/textfield/TextField';
import HourChooser from '../../editors/HourChooser';
import Render from '../../render/Render';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsAlt, dlsColor, dlsLayout, dlsZIndex} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {BoundaryMode} from '../../types/types';
import {LabelTypography} from '../../typography/LabelTypography';
import {CreateGeofenceCardContext} from './CreateGeofenceCard';
import {GeofenceRadius} from './GeofenceRadiusControl';
import {GeofenceSearchCreateCard} from './GeofenceSearchCreateCard';
import {GeofenceSearchVicinityCard} from './GeofenceSearchVicinityCard';
import SearchAddress from './SearchAddress';

type SearchProps = {
  view: __esri.MapView;
  isVisible: boolean;
  radius?: number;
  handleRadiusInput?: Function;
  drawingComplete: boolean;
  handleRadiusFromPanel?: Function;
  handleCompleteDrawing: Function;
  handleSetSelectedMapPoint?: Function;
  crumbs: Crumb[];
  selectCrumbs: Function;
  geofenceType: string;
  mapClickHandler?: any;
  handleBack?: Function;
  useHereApi?: boolean;
  isPhone?: boolean;
  boundaryMode?: BoundaryMode
  toggleShowBoundaryPanel?: Function;
  onSearchTimeChange?: Function;
};

const useStyles = makeStyles((theme) => ({
  container : {
    maxWidth   : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%': 285,
    maxHeight  : '600px',
    margin     : '10px',
    alignItems : 'center'
  },
  actionButtonContainer : {
    display        : 'flex',
    flexDirection  : 'row',
    justifyContent : 'space-between',
    alignItems     : 'center',
    marginTop      : toggleThemeStyle(undefined, '8px')
  },
  cancelButton : toggleThemeStyle({
    textAlign : 'center',
    width     : '50%'
  }, {
    display        : 'flex',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    width          : '50%'
  }),
  radialInput : {
    '&&' : {
      padding : '0 !important'
    }
  },
  distanceField : {
    width                          : toggleThemeStyle('60px', '80px'),
    marginLeft                     : dlsLayout('margins'),
    display                        : 'flex',
    alignSelf                      : 'flex-end',
    [theme.breakpoints.down('md')] : {
      marginTop    : 14,
      marginBottom : -26
    },
    [theme.breakpoints.up('md')] : {
      marginTop    : 8,
      marginBottom : -26
    }
  },
  radialInputUnderline : {
    '&::after' : {
      borderColor : dlsColor('caBlue')
    },
    '&::before' : {
      borderColor       : `${dlsColor('grey3')} !important`,
      borderBottomWidth : '1px !important'
    }
  },
  boxContainer : {
    display       : 'flex',
    flexDirection : 'column-reverse',
    marginBottom  : '0px'
  },
  sliderBox : {
    flexGrow : 1
  },
  slider : {
    width    : toggleThemeStyle(undefined, '100%'),
    maxWidth : ({isPhone}: {isPhone: boolean}) => isPhone ? '100%': 270
  },
  button : {
    width        : '90%',
    marginBottom : toggleThemeStyle('5px', undefined)
  },
  labelContainer : {
    backgroundColor : dlsColor('white'),
    display         : 'flex',
    alignItems      : 'center',
    padding         : '12px 0',
    gap             : '18px',
    height          : '50px',
    position        : 'relative'
  },
  labelText : {
    ...dlsAlt('typography', 'label3'),
    fontSize      : '14px !important',
    fontWeight    : 400,
    color         : dlsColor('grey2'),
    fontFamily    : 'Roboto',
    whiteSpace    : 'nowrap',
    textTransform : 'none'
  },
  dropdownWrapper : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '4px',
    maxWidth   : '150px',
    whiteSpace : 'nowrap'
  },
  customDropdownInput : {
    paddingRight : '1.2em !important',
    paddingLeft  : '2px !important',
    maxWidth     : '100%'
  },
  optionOverride : {
    fontSize  : '14px !important',
    padding   : '6px 10px !important',
    minHeight : '30px !important',
    color     : '#333 !important'
  },
  dropdownPopper : {
    minWidth                                               : '120px !important',
    border                                                 : '1px solid #ccc',
    boxShadow                                              : '0 2px 6px rgba(0,0,0,0.2)',
    position                                               : 'absolute !important',
    top                                                    : '100% !important',
    left                                                   : '0 !important',
    zIndex                                                 : `${dlsZIndex('dropdownPopper')} !important`,
    maxHeight                                              : '150px',
    backgroundColor                                        : dlsColor('white'),
    overflowY                                              : 'hidden',
    '& .MuiAutocomplete-paper, & .MuiAutocomplete-listbox' : {
      maxHeight                    : '150px',
      overflowY                    : 'auto',
      scrollbarWidth               : 'none',
      msOverflowStyle              : 'none',
      '&::-webkit-scrollbar'       : {width: '0px', height: '0px'},
      '&::-webkit-scrollbar-thumb' : {background: 'transparent'}
    }
  }
} as any));

const radiusMarks = [
  {
    value : 0,
    label : '0'
  },
  {
    value : 50,
    label : '50'
  },
  {
    value : 100,
    label : '100'
  },
  {
    value : 200,
    label : '200'
  },
  {
    value : 300,
    label : '300'
  }
];

const vicinityRadiusMarks = [
  {value: 1, label: '1'},
  {value: 2, label: '2'},
  {value: 3, label: '3'},
  {value: 4, label: '4'},
  {value: 5, label: '5'},
  {value: 6, label: '6'},
  {value: 7, label: '7'},
  {value: 8, label: '8'},
  {value: 9, label: '9'},
  {value: 10, label: '10'}
];

export function GeofenceSearch({
  view,
  isVisible,
  radius,
  handleRadiusInput,
  drawingComplete,
  handleRadiusFromPanel,
  handleCompleteDrawing,
  selectCrumbs,
  geofenceType,
  crumbs,
  mapClickHandler,
  handleBack,
  onSearchTimeChange,
  toggleShowBoundaryPanel,
  handleSetSelectedMapPoint,
  isPhone,
  boundaryMode,
  useHereApi = false
}: SearchProps) {
  const classes: any    = useStyles({isPhone});
  const [time, setTime] = useState(8);
  const createCard      = useContext(CreateGeofenceCardContext);
  const {isCurrent}     = useThemeContext();

  const onSearchTimeChanges = useCallback((value) => {
    let val = value;
    if (value !== '' && value < 1) val = 1;
    if (value !== '' && value > 24) val = 24;
    setTime(val);
    onSearchTimeChange?.(val);
  }, []);

  const sliderProps = useMemo(() => {
    if (boundaryMode === BoundaryMode.Vicinity) {
      return {
        step  : 1,
        max   : 10,
        marks : vicinityRadiusMarks
      };
    }
    return {
      step  : 1,
      max   : 300,
      marks : radiusMarks
    };
  }, [boundaryMode]);

  const endAdornment = useMemo(() => {
    if (boundaryMode === BoundaryMode.Vicinity) {
      const key = getUserUnit() === Unit.METRIC ? 'kilometers' : 'miles';
      return <span>{translate(`maps:geofences.${key}`)}</span>;
    }
    return <span>{translate('maps:geofences.meters')}</span>;
  }, [boundaryMode]);

  const onCancel = function() {
    toggleShowBoundaryPanel?.();
    handleBack?.();
    selectCrumbs(0, crumbs);
    mapClickHandler?.remove();
  };

  // the current theme's cards share one address search field - the vicinity
  // card drops its results over the card instead of growing it ...
  const isVicinityCard = isCurrent && boundaryMode === BoundaryMode.Vicinity;
  const cardSearch     = (
    <SearchAddress useHereApi={useHereApi}
      mapView={view}
      isSave
      hideLabel
      overlayResults={isVicinityCard}
      variant={isVicinityCard ? 'boxed' : 'default'}
      placeholder={isVicinityCard ? translate('maps:geofences.vicinityCard.searchPlaceholder') : undefined}
      geofenceType={geofenceType}
      isVisible={isVisible}
      handleSetSelectedMapPoint={handleSetSelectedMapPoint} />
  );

  // the current theme's single create geofence card (provided by
  // CreateGeofences only there, and never for boundary creation) ...
  const createCardView = createCard && (
    <GeofenceSearchCreateCard createCard={createCard}
      search={cardSearch}
      isRadial={geofenceType === translate('maps:geofences.radial')}
      radius={radius}
      sliderProps={sliderProps}
      isCreateDisabled={!drawingComplete}
      onCreate={handleCompleteDrawing as () => void}
      onRadiusChange={handleRadiusFromPanel as (meters: number) => void} />
  );

  // ... and its vicinity search card ...
  const vicinityCardView = isVicinityCard && (
    <GeofenceSearchVicinityCard search={cardSearch}
      radius={radius}
      sliderProps={sliderProps}
      hours={time}
      isSearchDisabled={!drawingComplete}
      onSearch={handleCompleteDrawing as () => void}
      onClose={onCancel}
      onHoursChange={onSearchTimeChanges}
      onRadiusSlide={handleRadiusFromPanel as GeofenceRadius['onSlide']}
      onRadiusInput={handleRadiusInput as GeofenceRadius['onInputChange']} />
  );

  return createCardView || vicinityCardView || (
    <div className={classes.container}>
      <SearchAddress useHereApi={useHereApi}
        mapView={view}
        isSave
        geofenceType={geofenceType}
        isVisible={isVisible}
        handleSetSelectedMapPoint={handleSetSelectedMapPoint} />
      {geofenceType === translate('maps:geofences.radial') && (
        <Box className={classes.boxContainer}>
          <Box className={classes.sliderBox}>
            <LabelTypography>{translate('maps:geofences.radius')}</LabelTypography>
            <Slider className={classes.slider}
              value={radius}
              {...sliderProps}
              onSlide={function(event, newValue) {
                handleRadiusFromPanel?.(newValue as number);
              }} />
          </Box>
          <Box className={classes.distanceField}>
            <TextField value={radius}
              onChange={handleRadiusInput}
              InputProps={{
                classes : {
                  input     : classes.radialInput,
                  underline : classes.radialInputUnderline
                },
                endAdornment
              }}
              inputProps={{
                maxLength : 7 // preventing user from adding unreal radius
              }} />
          </Box>
        </Box>)}
      <Render.If condition={boundaryMode === BoundaryMode.Vicinity}>
        <div className={classes.labelContainer}>
          <span className={classes.labelText}>Display for Past</span>
          <div className={classes.dropdownWrapper}>
            <HourChooser value={time}
              disabled={false}
              onChange={onSearchTimeChanges}
              {...({
                classes : {
                  customDropdownInput : classes.customDropdownInput,
                  optionOverride      : classes.optionOverride,
                  dropdownPopper      : classes.dropdownPopper
                }
              } as any)} />
          </div>
        </div>
      </Render.If>
      <div className={classes.actionButtonContainer}>
        <div className={classes.cancelButton}>
          <TertiaryButton noUnderline onClick={onCancel}>
            {translate('maps:geofences.cancel')}
          </TertiaryButton>
        </div>
        {geofenceType === translate('maps:geofences.polygon') ? (
          <PrimaryButton onClick={handleCompleteDrawing}
            disabled={!drawingComplete}
            className={classes.button}
            fullWidth>
            {`${translate('maps:geofences.next')}`}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={handleCompleteDrawing}
            disabled={!drawingComplete}
            className={classes.button}
            fullWidth>
            {`${translate('maps:geofences.next')}`}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
}
